import * as React from 'react';
import { Layout, Row, Col, Card } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import {
	projectTemplatesRestCrudThunksForBuyer,
	projectTemplatesRestCrudThunksForSupplier,
} from '../../thunks/project_template_thunks';
import { TwoLineList } from '../two_line_list/TwoLineList';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	getCurrency,
	getEntityById,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { mappingTemplateTypesForDisplay } from '../../utils/DataMappingUtils';
import { getFieldChoices, isMultiSelectField } from '../../utils/ProjectTemplateUtils';
import { formatCurrency } from '../../utils/DeliverableUtils';
import { isProjectsV2Enabled } from '../../utils/FeatureFlagUtils';

const { Content } = Layout;

const style = require('./ProjectTemplatesDetailDetailsPage.less');

// A field definition's type line. A multiple-choice field says how many answers may be
// picked and lists the options — the type alone ("multipleChoice") tells the reader
// nothing about what the field will actually ask.
const describeFieldType = (field) => {
	const choices = getFieldChoices(field);
	if (choices.length === 0) {
		return mappingTemplateTypesForDisplay(field);
	}
	return `${
		isMultiSelectField(field) ? 'Multiple choice (pick any)' : 'Multiple choice (pick one)'
	} — ${choices.join(', ')}`;
};

// A template item's time period, as it reads on the template itself: the project it is
// applied to supplies the actual dates.
const describeDuration = (item) => {
	const daysForDue = nullSafeGet('daysForDue', item);
	if (daysForDue === undefined || daysForDue === null || daysForDue === '') {
		return null;
	}
	return `Takes ${daysForDue} ${Number(daysForDue) === 1 ? 'day' : 'days'}`;
};

// A task's time period and default assignee on one muted line beside its title.
const describeTaskMeta = (task) =>
	[describeDuration(task), nullSafeGet('assignTo', task)].filter((part) => !!part).join(' · ');

interface ProjectTemplatesDetailPageProps {
	match: any;
}

class ProjectTemplatesDetailPage extends React.Component<any, any> {
	constructor(props) {
		super(props);
	}

	componentDidMount() {
		const { getProjectTemplate, match, currentUser } = this.props;
		const projectTemplateId = match.params.id;
		const userType = nullSafeGetOrElse('userType', currentUser, 'buyer');
		getProjectTemplate(userType, projectTemplateId);
	}

	render() {
		const { projectTemplate, projectTemplatesFetching, match, currentUser } = this.props;
		const projectTemplateId = match.params.id;
		const currency = getCurrency({ currentUser });
		const formatBudget = (amount) =>
			currency ? currency.symbolFormat(amount, 0) : formatCurrency(amount);
		const deliverables = nullSafeGetOrElse('deliverableTemplates', projectTemplate, []);
		const tasks = nullSafeGetOrElse('taskTemplates', projectTemplate, []);
		return projectTemplatesFetching ? (
			<PageLoadingPlaceholder />
		) : (
			<Content className="projectTemplatesDetailDetailsPage">
				{/*<ScrollToTopOnMount/>*/}
				{/*<BackTop/>*/}
				<Row style={{ margin: '-0.5em -8px 0.5em -8px' }} gutter={16}>
					<Col span={24}>
						<Card>
							<div className="projectTemplatesDetailDetailsPage__listSection">
								<div className="projectTemplatesDetailDetailsPage__listSectionTitle">
									Required Fields
								</div>
								{projectTemplate.requiredFields && projectTemplate.requiredFields.length === 0 ? (
									'No required fields'
								) : (
									<TwoLineList
										listItems={projectTemplate.requiredFields}
										lineOneRender={(record) => record.key}
										lineTwoRender={(record) => describeFieldType(record)}
									/>
								)}
							</div>
							<div className="projectTemplatesDetailDetailsPage__listSection">
								<div className="projectTemplatesDetailDetailsPage__listSectionTitle">
									Optional Fields
								</div>
								{projectTemplate.optionalFields && projectTemplate.optionalFields.length === 0 ? (
									'No optional fields'
								) : (
									<TwoLineList
										listItems={projectTemplate.optionalFields}
										lineOneRender={(record) => record.key}
										lineTwoRender={(record) => describeFieldType(record)}
									/>
								)}
							</div>
							{isProjectsV2Enabled() ? (
								<>
									<div className="projectTemplatesDetailDetailsPage__listSection">
										<div className="projectTemplatesDetailDetailsPage__listSectionTitle">
											Deliverables
										</div>
										{deliverables.length === 0
											? 'No deliverables'
											: deliverables.map((deliverable, index) => {
													const budget = nullSafeGet('budget', deliverable);
													const deliverableTasks = nullSafeGetOrElse('tasks', deliverable, []);
													const duration = describeDuration(deliverable);
													const assignees = nullSafeGetOrElse('assignTo', deliverable, []);
													// Resolved by id, so it survives rows being added or removed.
													const dependsOnId = nullSafeGet('dependsOnId', deliverable);
													const dependsOn = dependsOnId
														? deliverables.find(
																(candidate) => nullSafeGet('id', candidate) === dependsOnId
														  )
														: null;
													return (
														<div key={index} className="mb-4">
															<div className="flex items-center gap-2">
																<span className="bodymedium">
																	{nullSafeGet('name', deliverable)}
																</span>
																{budget != null ? (
																	<span className="footnoteregular text-character-secondary">
																		{formatBudget(budget)}
																	</span>
																) : null}
																{duration ? (
																	<span className="footnoteregular text-character-secondary">
																		{duration}
																	</span>
																) : null}
															</div>
															{assignees.length > 0 ? (
																<div className="footnoteregular text-character-secondary">
																	{`Assigned to ${assignees.join(', ')}`}
																</div>
															) : null}
															{dependsOn ? (
																<div className="footnoteregular text-character-secondary">
																	{`Depends on ${nullSafeGetOrElse(
																		'name',
																		dependsOn,
																		'Untitled deliverable'
																	)}`}
																</div>
															) : null}
															{nullSafeGet('description', deliverable) ? (
																<div className="footnoteregular text-character-secondary">
																	{nullSafeGet('description', deliverable)}
																</div>
															) : null}
															{deliverableTasks.length > 0 ? (
																<ul className="bodyregular mb-0 mt-1">
																	{deliverableTasks.map((task, taskIndex) => (
																		<li key={taskIndex}>
																			{nullSafeGet('title', task)}
																			{describeTaskMeta(task) ? (
																				<span className="footnoteregular ml-2 text-character-secondary">
																					{describeTaskMeta(task)}
																				</span>
																			) : null}
																		</li>
																	))}
																</ul>
															) : null}
														</div>
													);
											  })}
									</div>
									<div className="projectTemplatesDetailDetailsPage__listSection">
										<div className="projectTemplatesDetailDetailsPage__listSectionTitle">Tasks</div>
										{tasks.length === 0 ? (
											'No tasks'
										) : (
											<ul className="bodyregular mb-0">
												{tasks.map((task, index) => (
													<li key={index}>
														{nullSafeGet('title', task)}
														{describeTaskMeta(task) ? (
															<span className="footnoteregular ml-2 text-character-secondary">
																{describeTaskMeta(task)}
															</span>
														) : null}
													</li>
												))}
											</ul>
										)}
									</div>
								</>
							) : null}
						</Card>
					</Col>
				</Row>
			</Content>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	projectTemplatesFetching: state.project_templates.fetching,
	// Keyed to the id in the route — `detail` holds whichever template was fetched
	// last, so it can bleed a stale record in from a previous route.
	projectTemplate: getEntityById(ownProps.match.params.id, state.project_templates),
	currentUser: state.session.currentUser,
	match: ownProps.match,
	history: ownProps.history,
});

const mapDispatchToProps = (dispatch) => ({
	getProjectTemplate: (userType, id) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? projectTemplatesRestCrudThunksForSupplier.readOne(id)
				: projectTemplatesRestCrudThunksForBuyer.readOne(id)
		),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(ProjectTemplatesDetailPage)
);
